/**
 * Prevent body scroll and overscroll.
 * Tested on mac, iOS chrome / Safari, Android Chrome.
 *
 * Based on: https://benfrain.com/preventing-body-scroll-for-modals-in-ios/
 *           https://stackoverflow.com/a/41601290
 *
 * Use in combination with:
 * html, body {overflow: hidden;}
 *
 * and: -webkit-overflow-scrolling: touch; for the element that should scroll.
 *
 * disableBodyScroll(true, '.i-can-scroll');
 */

let _selector = false;
let _element = false;
let _clientY;
let _initialOverflow = null;
let _options = {};

/**
 * Polyfills for Element.matches and Element.closest
 */
if (typeof Element !== 'undefined' && !Element.prototype.matches) {
    Element.prototype.matches =
        Element.prototype.msMatchesSelector ||
        Element.prototype.webkitMatchesSelector;
}

if (typeof Element !== 'undefined' && !Element.prototype.closest) {
    Element.prototype.closest = function closest(s) {
        let el = this;
        if (!document.documentElement.contains(el)) return null;
        do {
            if (el.matches(s)) return el;
            el = el.parentElement || el.parentNode;
        } while (el !== null);
        return null;
    };
}

/**
 * Prevent default unless within _selector
 *
 * @param  event object event
 * @return void
 */
const preventBodyScroll = event => {
    if (_element === false || !event.target.closest(_selector)) {
        event.preventDefault();
    }
};

/**
 * Cache the clientY co-ordinates for
 * comparison
 *
 * @param  event object event
 * @return void
 */
const captureClientY = event => {
    // only respond to a single touch
    if (event.targetTouches.length === 1) {
        _clientY = event.targetTouches[0].clientY;
    }
};

const shouldTargetBeIgnored = target =>
    (_options.ignoredTargets || []).some(targetToIgnore =>
        target.matches(targetToIgnore),
    );

/**
 * Detect whether the element is at the top
 * or the bottom of their scroll and prevent
 * the user from scrolling beyond
 *
 * @param  event object event
 * @return void
 */
const preventOverscroll = event => {
    // only respond to a single touch
    if (event.targetTouches.length !== 1) {
        return;
    }

    if (shouldTargetBeIgnored(event.target)) {
        return;
    }

    const clientY = event.targetTouches[0].clientY - _clientY;

    // The element at the top of its scroll,
    // and the user scrolls down
    if (_element.scrollTop === 0 && clientY > 0) {
        event.preventDefault();
    }

    // The element at the bottom of its scroll,
    // and the user scrolls up
    // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#Problems_and_solutions
    if (
        _element.scrollHeight - _element.scrollTop <= _element.clientHeight &&
        clientY < 0
    ) {
        event.preventDefault();
    }
};

/**
 * Disable body scroll. Scrolling with the selector is
 * allowed if a selector is provided.
 *
 * @param  boolean allow
 * @param  string selector Selector to element to change scroll permission
 * @param  object Options object to configure
 * Available options :
 * - ignoredTargets: array of selectors to ignore when checking the scroll
 * @return void
 */
const disableBodyScroll = (allow, selector, options = {}) => {
    if (typeof selector !== 'undefined') {
        _selector = selector;
        _element = document.querySelector(selector);
    }

    _options = options;

    const body = document.body;
    if (allow === true) {
        if (_element !== false) {
            _element.addEventListener('touchstart', captureClientY, {
                passive: false,
            });
            _element.addEventListener('touchmove', preventOverscroll, {
                passive: false,
            });
        }
        _initialOverflow = body.style.overflow;
        body.style.overflow = 'hidden';
        body.addEventListener('touchmove', preventBodyScroll, {
            passive: false,
        });
    } else {
        if (_element !== false) {
            _element.removeEventListener('touchstart', captureClientY, {
                passive: false,
            });
            _element.removeEventListener('touchmove', preventOverscroll, {
                passive: false,
            });
        }
        body.style.overflow = _initialOverflow;
        body.removeEventListener('touchmove', preventBodyScroll, {
            passive: false,
        });
    }
};

export function disableScroll(selector, options = {}) {
    disableBodyScroll(true, selector, options);
}

export function enableScroll(selector, options = {}) {
    disableBodyScroll(false, selector, options);
}
